<section class="api-key-page">
  <!-- Top Bar: Standard 76px edge-to-edge header matching Documents and Templates -->
  <header class="page-topbar hero-section">
    <div class="topbar-title-block title-block">
      <h1 class="page-title">API Key</h1>
      <p class="page-subtitle helper-copy">
        API keys are a simpler alternative to using OAuth for connecting and authenticating BoldSign's API services.
      </p>
    </div>

    <button type="button" class="generate-btn btn-purple" (click)="openGenerateModal()">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span>Generate API key</span>
    </button>
  </header>

  <div class="api-key-body">
    <section class="keys-section" aria-labelledby="manage-keys-title">
      <!-- Section Heading Row -->
      <div class="section-heading-row">
        <div class="section-heading">
          <h2 id="manage-keys-title">Manage Keys</h2>
        </div>

        <div class="total-keys-count">
          <strong>{{ apiKeys.length }}</strong> <span>{{ keysCountLabel() }}</span>
        </div>
      </div>

      @if (feedbackMessage) {
        <div class="alert-banner success">
          <p class="status-note" aria-live="polite">{{ feedbackMessage }}</p>
        </div>
      }

      @if (errorMessage) {
        <div class="alert-banner error">
          <p class="status-note status-note-error" aria-live="polite">{{ errorMessage }}</p>
        </div>
      }

      <div class="table-container table-card">
        <div class="table-scroll">
          <table class="c-table">
            <thead>
              <tr>
                <th class="name-col">NAME</th>
                <th class="key-col">API KEY</th>
                <th class="env-col">ENVIRONMENT</th>
                <th class="date-col">CREATED ON</th>
                <th class="valid-col">VALID TILL</th>
              </tr>
            </thead>
            <tbody>
              @if (loading) {
                <tr>
                  <td colspan="5" class="c-table-loading-cell empty-row">
                    <div class="c-loading-state">
                      <div class="c-spinner"></div>
                      <p class="c-loading-text">Loading API keys…</p>
                    </div>
                  </td>
                </tr>
              } @else if (apiKeys.length === 0) {
                <tr>
                  <td colspan="5" class="empty-row">
                    <div class="empty-state-wrap">
                      <div class="empty-state-icon">
                        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8">
                          <path d="M21 2l-2 2m-1.5 1.5L14 9l-1.5-1.5L11 9l-1.5-1.5L8 9 4.5 5.5a4.95 4.95 0 0 0-7 7L1 16l4.5 4.5a4.95 4.95 0 0 0 7-7L16 10l-1.5-1.5 1.5-1.5L14.5 5.5 16 4l2-2h3v3z"></path>
                        </svg>
                      </div>
                      <p class="empty-state-text">No API keys found.</p>
                      <p class="empty-state-sub">Generate an API key to securely connect and authenticate with our API services.</p>
                      <button type="button" class="generate-btn btn-purple" (click)="openGenerateModal()">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span>Generate API key</span>
                      </button>
                    </div>
                  </td>
                </tr>
              } @else {
                @for (apiKey of apiKeys; track trackByApiKey($index, apiKey)) {
                  <tr class="api-key-row">
                    <td class="name-cell">
                      <span class="key-name">{{ apiKey.name }}</span>
                    </td>
                    <td class="key-cell">
                      <div class="api-key-wrap">
                        <span class="api-key-value">{{ apiKey.apiKey }}</span>
                        <button
                          type="button"
                          class="api-key-copy-btn"
                          [class.copied]="copiedKeyId === apiKey.id"
                          [title]="copiedKeyId === apiKey.id ? 'Copied!' : 'Copy API key'"
                          [attr.aria-label]="copiedKeyId === apiKey.id ? 'Copied API key' : 'Copy API key'"
                          (click)="copyApiKey(apiKey, $event)"
                        >
                          @if (copiedKeyId === apiKey.id) {
                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="copy-check-icon">
                              <polyline points="4 10 8 14 16 6"></polyline>
                            </svg>
                          } @else {
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="copy-svg-icon">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          }
                        </button>
                      </div>
                    </td>
                    <td class="env-cell">
                      <span class="env-badge env-{{ apiKey.environment }}">
                        {{ formatEnvironment(apiKey.environment) }}
                      </span>
                    </td>
                    <td class="date-cell">
                      <span class="date-text">{{ formatDateTime(apiKey.createdAt) }}</span>
                    </td>
                    <td class="valid-cell">
                      <div class="valid-cell-wrap">
                        <span class="date-text">{{ apiKey.expiresAt ? formatDateTime(apiKey.expiresAt) : '—' }}</span>
                        <div class="row-actions">
                          <button type="button" class="row-action-btn edit-btn" (click)="openEditModal(apiKey)">
                            Edit
                          </button>
                          <button type="button" class="row-action-btn row-action-btn--danger delete-btn" (click)="openDeleteConfirm(apiKey)">
                            Delete
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>

  <!-- Generate API Key Modal -->
  <section
    class="generate-modal-mask modal-mask"
    [class.generate-modal-mask--open]="generateModalOpen"
    [class.modal-mask--open]="generateModalOpen"
    [attr.aria-hidden]="!generateModalOpen"
    role="presentation"
    (click)="generateModalOpen && closeGenerateModal()"
  >
    <div
      class="generate-modal modal-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-api-key-title"
      (click)="$event.stopPropagation()"
    >
      <div class="generate-modal-body modal-body">
        <header class="generate-modal-header modal-header">
          <div class="generate-modal-heading">
            <h3 id="generate-api-key-title">Generate API key</h3>

            <div class="environment-toggle" role="group" aria-label="API key environment">
              <button
                type="button"
                class="environment-option"
                [class.active]="environment === 'sandbox'"
                (click)="setEnvironment('sandbox')"
              >
                SANDBOX
              </button>
              <label class="switch switch--compact">
                <input
                  type="checkbox"
                  [checked]="environment === 'live'"
                  (change)="setEnvironment($any($event.target).checked ? 'live' : 'sandbox')"
                />
                <span class="visually-hidden">Toggle live environment</span>
              </label>
              <button
                type="button"
                class="environment-option"
                [class.active]="environment === 'live'"
                (click)="setEnvironment('live')"
              >
                LIVE
              </button>
            </div>
          </div>

          <button type="button" class="modal-close-btn" (click)="closeGenerateModal()" aria-label="Close generate API key modal">
            ×
          </button>
        </header>

        <p class="generate-modal-copy modal-copy">API keys can be used to authenticate BoldSign's API services</p>

        <div class="generate-form modal-form">
          <label class="form-field">
            <span class="form-label">API Key Name*</span>
            <input type="text" [(ngModel)]="apiKeyName" placeholder="Enter name..." />
          </label>

          <div class="validity-section">
            <label class="switch validity-toggle">
              <span class="validity-label">Token Validity</span>
              <input
                type="checkbox"
                [checked]="tokenValidityEnabled"
                (change)="setTokenValidity($any($event.target).checked)"
              />
            </label>

            <p class="field-hint">Enable this option to set an expiry date for the API token</p>

            <label class="form-field form-field--date" [class.is-hidden]="!tokenValidityEnabled">
              <span class="form-label">Expiry Date</span>
              <input type="date" [(ngModel)]="expiryDate" [min]="minExpiryDate" />
            </label>

            @if (expiryDateError) {
              <p class="modal-error" aria-live="polite">{{ expiryDateError }}</p>
            }
          </div>

          <section class="sandbox-note" [class.is-hidden]="!isSandbox" aria-label="Sandbox warning">
            <div class="sandbox-note-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="sandbox-note-text">
              <p>Documents created in the sandbox environment are automatically deleted after 14 days.</p>
              <p>
                Documents generated in the sandbox environment include a TEST watermark and cannot be used as legally valid documents.
              </p>
            </div>
          </section>

          @if (generateModalError) {
            <p class="modal-error" aria-live="polite">{{ generateModalError }}</p>
          }
        </div>
      </div>

      <footer class="generate-modal-actions modal-footer">
        <button type="button" class="ghost-btn" (click)="closeGenerateModal()">Cancel</button>
        <button type="button" class="primary-btn btn-purple" [disabled]="!canGenerateToken" (click)="submitGenerateToken()">
          {{ generateModalSubmitting ? 'Generating…' : 'Generate Token' }}
        </button>
      </footer>
    </div>
  </section>

  <!-- Edit API Key Modal -->
  <section
    class="edit-modal-mask modal-mask"
    [class.edit-modal-mask--open]="editModalOpen"
    [class.modal-mask--open]="editModalOpen"
    [attr.aria-hidden]="!editModalOpen"
    role="presentation"
    (click)="editModalOpen && closeEditModal()"
  >
    <div class="edit-modal modal-card" role="dialog" aria-modal="true" aria-labelledby="edit-api-key-title" (click)="$event.stopPropagation()">
      <header class="edit-modal-header modal-header">
        <h3 id="edit-api-key-title">Edit API Key</h3>
        <button type="button" class="modal-close-btn" (click)="closeEditModal()" aria-label="Close edit API key modal">×</button>
      </header>

      <div class="edit-modal-body modal-body">
        <label class="form-field">
          <span class="form-label">API Key Name*</span>
          <input type="text" [(ngModel)]="editName" placeholder="Enter name..." />
        </label>

        @if (editModalError) {
          <p class="modal-error" aria-live="polite">{{ editModalError }}</p>
        }
      </div>

      <footer class="edit-modal-actions modal-footer">
        <button type="button" class="ghost-btn" (click)="closeEditModal()">Cancel</button>
        <button type="button" class="primary-btn btn-purple" [disabled]="!canSaveEdit" (click)="submitEdit()">
          {{ editModalSubmitting ? 'Saving…' : 'Save' }}
        </button>
      </footer>
    </div>
  </section>

  <!-- Delete Confirm Modal -->
  <section
    class="delete-modal-mask modal-mask"
    [class.delete-modal-mask--open]="!!deleteTarget"
    [class.modal-mask--open]="!!deleteTarget"
    [attr.aria-hidden]="!deleteTarget"
    role="presentation"
    (click)="deleteTarget && closeDeleteConfirm()"
  >
    <div class="delete-modal modal-card" role="dialog" aria-modal="true" aria-labelledby="delete-api-key-title" (click)="$event.stopPropagation()">
      <div class="delete-modal-body modal-body">
        <div class="delete-icon-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </div>
        <h3 id="delete-api-key-title">Delete API key?</h3>
        <p class="delete-copy">Are you sure you want to permanently delete this API key? This action cannot be undone.</p>
        @if (deleteTarget; as apiKeyToDelete) {
          <p class="delete-modal-detail"><strong>{{ apiKeyToDelete.name }}</strong></p>
        }
      </div>

      <footer class="delete-modal-actions modal-footer">
        <button type="button" class="ghost-btn" (click)="closeDeleteConfirm()" [disabled]="deleteSubmitting">Cancel</button>
        <button type="button" class="danger-btn" (click)="confirmDelete()" [disabled]="deleteSubmitting">
          {{ deleteSubmitting ? 'Deleting…' : 'Delete' }}
        </button>
      </footer>
    </div>
  </section>

  <!-- Token Created Modal (One-Time Token View) -->
  <section
    class="token-modal-mask modal-mask"
    [class.token-modal-mask--open]="createdTokenModalOpen"
    [class.modal-mask--open]="createdTokenModalOpen"
    [attr.aria-hidden]="!createdTokenModalOpen"
    role="presentation"
    (click)="createdTokenModalOpen && closeCreatedTokenModal()"
  >
    <div class="token-modal modal-card" role="dialog" aria-modal="true" aria-labelledby="api-key-created-title" (click)="$event.stopPropagation()">
      <header class="token-modal-header modal-header">
        <h3 id="api-key-created-title">API Key Created</h3>
        <button type="button" class="modal-close-btn" (click)="closeCreatedTokenModal()" aria-label="Close created API key modal">
          ×
        </button>
      </header>

      <div class="token-modal-body modal-body">
        <p class="token-modal-copy modal-copy">
          Store this API key securely. You won't be able to view the full token again after closing this dialog.
        </p>
        <label class="form-field">
          <span class="form-label">{{ createdTokenName || 'Generated API Key' }}</span>
          <div class="token-input-shell">
            <input type="text" class="token-display" [value]="createdTokenValue" readonly aria-readonly="true" />
            <button
              type="button"
              class="token-copy-icon-btn"
              [disabled]="copyCreatedTokenPending"
              [attr.aria-label]="createdTokenCopied ? 'API key copied' : 'Copy API key'"
              (click)="copyCreatedToken()"
            >
              <span aria-hidden="true">{{ createdTokenCopied ? '✓' : '⧉' }}</span>
            </button>
          </div>
          @if (createdTokenCopyError) {
            <p class="modal-error" aria-live="polite">{{ createdTokenCopyError }}</p>
          }
        </label>
      </div>

      <footer class="token-modal-actions modal-footer">
        <button type="button" class="primary-btn btn-purple" (click)="closeCreatedTokenModal()">Done</button>
      </footer>
    </div>
  </section>
</section>
